import { TrendingUp, CheckCircle, XCircle, Loader, AlertCircle, Sparkles } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { CompletenessProgress } from '../../types/completenessGap';

interface CompletenessProgressHeaderProps {
  progress: CompletenessProgress;
  onAnalyze?: () => void;
  isAnalyzing?: boolean;
}

export default function CompletenessProgressHeader({ progress, onAnalyze, isAnalyzing }: CompletenessProgressHeaderProps) {
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-600';
    if (score >= 70) return 'bg-amber-600';
    return 'bg-red-600';
  };

  const scoreColor = getScoreColor(progress.currentScore);
  const scoreBgColor = getScoreBgColor(progress.currentScore);

  return (
    <Card className="bg-gradient-to-br from-primary-50 via-white to-secondary-50 border-2 border-primary-100">
      <div className="p-6">
        {/* Header with Analyze Completeness Button */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-neutral-900">
            Data Requirements Completeness
          </h3>
          {onAnalyze && (
            <Button
              variant="primary"
              size="sm"
              onClick={onAnalyze}
              loading={isAnalyzing}
              disabled={isAnalyzing}
              className="flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              {isAnalyzing ? 'Analyzing...' : 'Analyze Completeness'}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: Score and Progress Bar */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-medium text-neutral-600 mb-1">
                  Completeness Score
                </h3>
                <div className="flex items-center gap-3">
                  <div className={`text-4xl font-bold ${scoreColor}`}>
                    {progress.currentScore}%
                  </div>
                  {progress.initialScore !== null && progress.initialScore !== undefined && progress.initialScore !== progress.currentScore && (
                    <div className="flex items-center gap-2 text-sm">
                      <div className="text-neutral-500">
                        <span className="text-xs">Started at:</span>
                        <div className="text-lg font-semibold text-neutral-700">
                          {progress.initialScore}%
                        </div>
                      </div>
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-neutral-500 mb-1">Target</div>
                <div className="text-2xl font-semibold text-primary-600">
                  {progress.targetScore}%
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-neutral-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${scoreBgColor}`}
                style={{ width: `${progress.currentScore}%` }}
              />
            </div>

            {/* Progress Improvement or Estimated Actions */}
            {progress.initialScore !== null && progress.initialScore !== undefined && progress.initialScore !== progress.currentScore ? (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <div className="flex items-center gap-1 text-green-600">
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-semibold">
                    +{progress.currentScore - progress.initialScore} points
                  </span>
                </div>
                <span className="text-neutral-600">
                  improvement since first analysis
                </span>
              </div>
            ) : progress.estimatedActionsToComplete > 0 && (
              <div className="mt-3 flex items-center gap-2 text-sm text-neutral-600">
                <TrendingUp className="w-4 h-4" />
                <span>
                  <strong className="text-primary-600">
                    {progress.estimatedActionsToComplete}
                  </strong>{' '}
                  {progress.estimatedActionsToComplete === 1 ? 'action' : 'actions'} to reach {progress.targetScore}%
                </span>
              </div>
            )}
          </div>

          {/* Right: Gap Statistics */}
          <div>
            <h4 className="text-sm font-medium text-neutral-600 mb-3">
              Gap Status Overview
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {/* Pending Gaps */}
              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <div className="text-xs text-amber-700">Pending</div>
                  <div className="text-lg font-bold text-amber-900">
                    {progress.pendingGaps}
                  </div>
                </div>
              </div>

              {/* In Progress */}
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <Loader className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <div className="text-xs text-blue-700">In Progress</div>
                  <div className="text-lg font-bold text-blue-900">
                    {progress.inProgressGaps}
                  </div>
                </div>
              </div>

              {/* Resolved */}
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div>
                  <div className="text-xs text-green-700">Resolved</div>
                  <div className="text-lg font-bold text-green-900">
                    {progress.resolvedGaps}
                  </div>
                </div>
              </div>

              {/* Dismissed */}
              <div className="flex items-center gap-2 p-2 bg-neutral-50 rounded-lg border border-neutral-200">
                <XCircle className="w-5 h-5 text-neutral-500 flex-shrink-0" />
                <div>
                  <div className="text-xs text-neutral-600">Dismissed</div>
                  <div className="text-lg font-bold text-neutral-900">
                    {progress.dismissedGaps}
                  </div>
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            {progress.gapsByCategory && Object.keys(progress.gapsByCategory).length > 0 && (
              <div className="mt-3 pt-3 border-t border-neutral-200">
                <div className="text-xs text-neutral-500 mb-2">By Category:</div>
                <div className="flex items-center gap-3 text-xs">
                  {progress.gapsByCategory.why_not_100 !== undefined && (
                    <span className="text-neutral-700">
                      📊 Why Not 100%:{' '}
                      <strong className="text-neutral-900">
                        {progress.gapsByCategory.why_not_100}
                      </strong>
                    </span>
                  )}
                  {progress.gapsByCategory.missing_data !== undefined && (
                    <span className="text-neutral-700">
                      🔍 Missing Data:{' '}
                      <strong className="text-neutral-900">
                        {progress.gapsByCategory.missing_data}
                      </strong>
                    </span>
                  )}
                  {progress.gapsByCategory.clarification !== undefined && (
                    <span className="text-neutral-700">
                      ❓ Clarification:{' '}
                      <strong className="text-neutral-900">
                        {progress.gapsByCategory.clarification}
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
